import { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faLinkedin,
  faFacebook,
  faInstagram,
  faXTwitter,
  faYoutube,
  faTiktok,
  faPinterest,
  faThreads,
  faGoogle,
  faBluesky,
} from '@fortawesome/free-brands-svg-icons';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/utils/cn';
import { filterTriggerHover } from './filterTriggerStyles';

/**
 * PlatformSelect Component
 *
 * Dropdown select for social media platforms with brand icons and colors
 * Matches the styling used in ChannelSettings
 */

interface PlatformInfo {
  value: string;
  label: string;
  color: string;
  icon: IconDefinition;
}

export const PLATFORMS: PlatformInfo[] = [
  { value: 'LINKEDIN', label: 'LinkedIn', color: '#0A66C2', icon: faLinkedin },
  { value: 'FACEBOOK', label: 'Facebook', color: '#1877F2', icon: faFacebook },
  { value: 'INSTAGRAM', label: 'Instagram', color: '#E4405F', icon: faInstagram },
  { value: 'TWITTER', label: 'X (Twitter)', color: '#000000', icon: faXTwitter },
  { value: 'YOUTUBE', label: 'YouTube', color: '#FF0000', icon: faYoutube },
  { value: 'TIKTOK', label: 'TikTok', color: '#000000', icon: faTiktok },
  { value: 'PINTEREST', label: 'Pinterest', color: '#E60023', icon: faPinterest },
  { value: 'THREADS', label: 'Threads', color: '#000000', icon: faThreads },
  { value: 'GOOGLE_MY_BUSINESS', label: 'Google Business', color: '#4285F4', icon: faGoogle },
  { value: 'BLUESKY', label: 'Bluesky', color: '#0085FF', icon: faBluesky },
];

interface PlatformSelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  showAllOption?: boolean;
  allOptionLabel?: string;
  disabled?: boolean;
  className?: string;
  /** Platform values to exclude from the list */
  exclude?: string[];
  size?: 'sm' | 'md';
}

export const PlatformSelect = ({
  value,
  onChange,
  placeholder = 'Select platform',
  showAllOption = true,
  allOptionLabel = 'All Channels',
  disabled = false,
  className,
  exclude = [],
  size = 'md',
}: PlatformSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Consume the key so a surrounding drawer/modal does not also close.
        e.preventDefault();
        setIsOpen(false);
      }
    };

    // Capture phase: runs before the bubble-phase Escape listeners of enclosing layers.
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen]);

  const selectedPlatform = PLATFORMS.find((p) => p.value === value);
  const isAllSelected = !value || value === 'ALL';

  const handleSelect = (platformValue: string) => {
    onChange(platformValue);
    setIsOpen(false);
  };

  return (
    <div className={cn('relative', className)} ref={ref}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          size === 'sm' ? 'w-full px-3 h-[34px] text-left text-xs lg:h-auto lg:px-4 lg:py-3 lg:text-sm' : 'w-full px-4 py-3 text-left text-sm',
          'border border-gray-200 rounded-lg bg-white',
          'focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent',
          !disabled && filterTriggerHover,
          'transition-all duration-200',
          'flex items-center justify-between gap-2',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <span className="flex items-center gap-2 min-w-0">
          {isAllSelected ? (
            <>
              <span className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                <FontAwesomeIcon icon={faGlobe} className="text-gray-500 text-[10px]" />
              </span>
              <span className="truncate">{allOptionLabel}</span>
            </>
          ) : selectedPlatform ? (
            <>
              <span
                className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: selectedPlatform.color }}
              >
                <FontAwesomeIcon
                  icon={selectedPlatform.icon}
                  className="text-white text-[10px]"
                />
              </span>
              <span className="truncate">{selectedPlatform.label}</span>
            </>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </span>
        <svg
          className={cn(
            'w-4 h-4 text-gray-400 transition-transform flex-shrink-0',
            isOpen && 'rotate-180'
          )}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-dropdown mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-auto">
          {/* All option */}
          {showAllOption && (
            <button
              type="button"
              onClick={() => handleSelect('ALL')}
              className={cn(
                'w-full px-3 py-2 text-left text-sm flex items-center gap-2',
                'hover:bg-gray-50 transition-colors',
                isAllSelected && 'bg-primary-50 text-primary-700 font-medium'
              )}
            >
              <span className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                <FontAwesomeIcon icon={faGlobe} className="text-gray-500 text-[10px]" />
              </span>
              <span>{allOptionLabel}</span>
            </button>
          )}

          {/* Platform options */}
          {PLATFORMS.filter((p) => !exclude.includes(p.value)).map((platform) => (
            <button
              key={platform.value}
              type="button"
              onClick={() => handleSelect(platform.value)}
              className={cn(
                'w-full px-3 py-2 text-left text-sm flex items-center gap-2',
                'hover:bg-gray-50 transition-colors',
                value === platform.value && 'bg-primary-50 text-primary-700 font-medium'
              )}
            >
              <span
                className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: platform.color }}
              >
                <FontAwesomeIcon icon={platform.icon} className="text-white text-[10px]" />
              </span>
              <span>{platform.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
